import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import usePastTime from 'hooks/usePastTime';
import { DealRoom } from 'models/DealRoom';
import { PortfolioSellerTable } from './data';
import Tag from 'components/ListingPortfolioCard/components/Tag';
import {
  DEAL_STATUS_COLOR,
  DEAL_STATUS_LABEL,
  LISTING_STATUS_COLOR,
  LISTING_STATUS_LABEL,
} from 'data/dealStatus';
import { useNavigate } from 'react-router-dom';
import {
  checkIsUserSuperAdmin,
  formatPercentage,
  getAskingPrice,
  getDataRoomRoute,
  getIsListingOwner,
  getJoinedWordsWithComma,
  getListingStage,
  getSellingPortfolio,
  getUserFullName,
} from 'utils';
import { DATA_ROOM_LOGIN_SOURCE } from 'models/DataRoomLoginSource';
import { GetBuyersBanner } from 'components/GetBuyersBanner';
import ThreeDotsDropdown from 'components/ThreeDotsDropdown';
import LockclosedIcon from 'icons/Lockclosed.icon';
import LockopenIcon from 'icons/Lockopen.icon';
import usePremiumFeatures from 'hooks/usePremiumFeatures';
import { ALIGNMENT } from 'models/Global';
import useViewPortfolioAs from 'hooks/useViewPortfolioAs';
import FloatingBanner from 'components/FloatingBanner';
import ListingMainImageWithUploadOption from '../../../../../../components/ListingMainImageWithUploadOption';
import { DropzoneFile } from 'components/Dropzone/utils';
import { RESIZED_WIDTH } from 'components/ResolveImage/utils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useCanAccessDataRoom from 'hooks/useCanAccessDataRoom';

type CellProps = {
  deal: DealRoom;
  showBannerNotification?: boolean;
  updateDeal: (id: string, deal: DealRoom) => void;
  updateGallery: (dealId: string, gallery: DropzoneFile[]) => void;
};

const TitleCell: React.FC<CellProps> = ({ deal, updateGallery }) => {
  const { portfolioName, listing, isRWC } = deal || {};

  const { currentUser, isSuperAdmin } = useContext(CurrentUserContext);

  const allowListingImageUpload =
    isSuperAdmin ||
    getIsListingOwner(currentUser?._id, listing?.userId, listing?.isCoOwner);

  return (
    <div className="pdt-name-cell">
      <ListingMainImageWithUploadOption
        listing={listing}
        width={RESIZED_WIDTH[320]}
        isRWC={isRWC}
        isDisabledUpload={!allowListingImageUpload}
        updateGallery={updateGallery}
      />
      <span>{portfolioName}</span>
    </div>
  );
};

const DealStatusCell: React.FC<CellProps> = ({ deal }) => {
  const { stage } = deal;

  const { t } = useTranslation();

  return (
    <Tag
      variant={DEAL_STATUS_COLOR[stage]}
      content={t(DEAL_STATUS_LABEL[stage])}
    />
  );
};

const ListingStatusCell: React.FC<CellProps> = ({ deal }) => {
  const { listingStage, wantToBePrivate } = deal;

  const { t } = useTranslation();

  const stage = getListingStage(listingStage, wantToBePrivate);

  return (
    <Tag
      content={t(LISTING_STATUS_LABEL[stage])}
      variant={LISTING_STATUS_COLOR[stage]}
    />
  );
};

const CommisionCell: React.FC<CellProps> = ({ deal }) => {
  const { franchisorFeePercentage } = deal;

  if (!franchisorFeePercentage) return null;

  return (
    <div className="pdt-commission-cell">
      <div>{formatPercentage(franchisorFeePercentage)}</div>
    </div>
  );
};

const PriceCell: React.FC<CellProps> = ({ deal }) => {
  const { amount, amountFrom, amountTo, priceGuide } = deal;

  const askingPrice = getAskingPrice({
    amount,
    amountFrom,
    amountTo,
    askingPriceSelect: priceGuide,
  });

  if (!askingPrice) return null;

  return <div className="pdt-price-cell">{askingPrice}</div>;
};

const LocationCell: React.FC<CellProps> = ({ deal }) => {
  const { location } = deal;

  if (!location) return null;

  return (
    <div className="pdt-location-cell">
      <div>{location}</div>
    </div>
  );
};

const ListingFiles: React.FC<CellProps> = ({ deal }) => {
  const { dataRoomId, filesSort, hasDataRoomAccess } = deal || {};

  const canAccessDataRoom = useCanAccessDataRoom()(deal, hasDataRoomAccess);

  const navigate = useNavigate();

  const handleOnFilesClick = useCallback(
    (e) => {
      if (!filesSort || !dataRoomId || !canAccessDataRoom) return;

      e.stopPropagation();
      navigate(getDataRoomRoute(dataRoomId, 'documents', DATA_ROOM_LOGIN_SOURCE.PORTFOLIO));
    },
    [filesSort, dataRoomId, canAccessDataRoom, navigate],
  );

  return (
    <div className="pdt-files-cell" onClick={handleOnFilesClick}>
      <div className="number">{filesSort}</div>
    </div>
  );
};

const ListingLeads: React.FC<CellProps> = ({
  deal,
  showBannerNotification,
}) => {
  const { _id, buyersSort, listing } = deal;

  const navigate = useNavigate();

  const onClick = useCallback(
    (e) => {
      e.stopPropagation();
      navigate(getSellingPortfolio(_id, 'leads'));
    },
    [_id, navigate],
  );

  return (
    <div className="pdt-files-cell" onClick={onClick}>
      <FloatingBanner
        showBannerNotification={showBannerNotification}
        hasCloseButton={false}
        anchor={<div className="number">{buyersSort}</div>}
      >
        <GetBuyersBanner
          direction="column"
          dealRoomId={_id}
          listingId={listing?._id}
          listingType={listing?.type}
          hasImage={false}
        />
      </FloatingBanner>
    </div>
  );
};

const UpdatedAtCellComponent: React.FC<CellProps> = ({ deal }) => {
  const date = deal.lastUpdated;

  const pastTime = usePastTime()(date);

  if (!date) return null;

  return <div className="pdt-published-cell">{pastTime}</div>;
};

const OwnerCell: React.FC<CellProps> = ({ deal }) => {
  const { _id, firstName, lastName, role } = deal.user;

  const isUserSuperAdmin = checkIsUserSuperAdmin(role);

  const navigateToUsersPortfolio = useViewPortfolioAs();

  const { t } = useTranslation();

  const fullName = getUserFullName(firstName, lastName);

  return (
    <div
      className="pdt-owner-cell"
      onClick={(e) => {
        e.stopPropagation();
        !isUserSuperAdmin && navigateToUsersPortfolio(_id, fullName);
      }}
    >
      {isUserSuperAdmin ? t('resolve') : fullName}
    </div>
  );
};

const LeadBrokerCell: React.FC<CellProps> = ({ deal }) => {
  const { leadBrokers } = deal;

  const leadBrokersNames = getJoinedWordsWithComma(
    leadBrokers?.map(({ name }) => name),
  );

  if (!leadBrokersNames) return null;

  return <div className="pdt-broker-name-cell">{leadBrokersNames}</div>;
};

const CompanyCell: React.FC<CellProps> = ({ deal }) => {
  const { leadBrokers } = deal;

  const leadBroker = leadBrokers?.[0];

  const { user } = leadBroker || {};

  if (!user) return null;

  return <div className="pdt-company-cell">{user.companyName}</div>;
};

const CoBrokerCell: React.FC<CellProps> = ({ deal }) => {
  const { coBrokers } = deal;

  const coBrokersNames = getJoinedWordsWithComma(
    coBrokers?.map(({ name }) => name),
  );

  if (!coBrokersNames) return null;

  return <div className="pdt-co-broker-cell">{coBrokersNames}</div>;
};

const IndustryCellComponent: React.FC<CellProps> = ({ deal }) => {
  const { industry } = deal;

  return <div className="pdt-industry-cell">{industry}</div>;
};

const ManageCellComponent: React.FC<CellProps> = ({ deal, updateDeal }) => {
  const { _id } = deal;

  const {
    canLockPremiumFeatures,
    canUnlockPremiumFeatures,
    updateHasPremiumFeatures,
  } = usePremiumFeatures(deal, (hasBossModeUnlockedPremiumFeatures) =>
    updateDeal(_id, {
      ...deal,
      hasBossModeUnlockedPremiumFeatures,
    }),
  );

  const { t } = useTranslation();

  const options = useMemo(
    () => [
      ...(canUnlockPremiumFeatures
        ? [
            {
              label: t('unlockAllFeatures'),
              icon: <LockopenIcon />,
              onClick: () => updateHasPremiumFeatures(true),
            },
          ]
        : []),
      ...(canLockPremiumFeatures
        ? [
            {
              label: t('lockAllFeatures'),
              icon: <LockclosedIcon />,
              onClick: () => updateHasPremiumFeatures(false),
            },
          ]
        : []),
    ],
    [
      canLockPremiumFeatures,
      canUnlockPremiumFeatures,
      t,
      updateHasPremiumFeatures,
    ],
  );

  if (!options.length) return null;

  return (
    <div onClick={(e) => e.stopPropagation()} className="pdt-manage-cell">
      <ThreeDotsDropdown
        renderAsPortal
        withBorder={false}
        alignment={ALIGNMENT.VERTICAL}
        options={options}
      />
    </div>
  );
};

export const BODY_CONTENT_GENERATE: Record<
  keyof PortfolioSellerTable,
  (cellProps: CellProps) => React.ReactNode
> = {
  listing: (cellProps: CellProps) => <TitleCell {...cellProps} />,
  stage: (cellProps: CellProps) => <DealStatusCell {...cellProps} />,
  listingStage: (cellProps: CellProps) => <ListingStatusCell {...cellProps} />,
  commission: (cellProps: CellProps) => <CommisionCell {...cellProps} />,
  location: (cellProps: CellProps) => <LocationCell {...cellProps} />,
  filesSort: (cellProps: CellProps) => <ListingFiles {...cellProps} />,
  buyersSort: (cellProps: CellProps) => <ListingLeads {...cellProps} />,
  lastUpdated: (cellProps: CellProps) => (
    <UpdatedAtCellComponent {...cellProps} />
  ),
  ownerName: (cellProps: CellProps) => <OwnerCell {...cellProps} />,
  leadBrokerName: (cellProps: CellProps) => <LeadBrokerCell {...cellProps} />,
  coBrokerName: (cellProps: CellProps) => <CoBrokerCell {...cellProps} />,
  companyName: (cellProps: CellProps) => <CompanyCell {...cellProps} />,
  industry: (cellProps: CellProps) => <IndustryCellComponent {...cellProps} />,
  price: (cellProps: CellProps) => <PriceCell {...cellProps} />,
  manage: (cellProps: CellProps) => <ManageCellComponent {...cellProps} />,
};
